<template>
  <ts-modal
    ref="modal"
    modal-class="modal-confirmation"
    :modal-title="confirmation.title"
    :modal-dialog-class="confirmation.modalDialogClass ?? undefined"
  >
    <template #modal-content>
      <slot
        name="confirmation"
        :context="confirmation.context"
      >
        <alert
          :message="confirmation.message"
          :dismissable="false"
          additional-css-class="text-center"
          :type="confirmation.type"
        />
      </slot>
      <div
        v-if="requireConfirmationCode"
        class="confirmation"
      >
        <div class="code">
          Enter confirmation code to proceed <span class="code">{{ confirmation.confirmationCode }}</span>
        </div>
        <div class="mb-3">
          <input
            v-model="confirmationCode"
            type="text"
            :class="['form-control form-control-sm', {'is-invalid': confirmationCodeInvalid}]"
          >
          <div
            v-if="confirmationCodeInvalid"
            class="invalid-feedback"
          >
            Invalid confirmation code
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        {{ confirmation.cancelActionTitle }}
      </button>
      <button
        :class="`btn btn-sm btn-${confirmation.type}`"
        @click="onConfirmAction"
      >
        {{ confirmation.confirmActionTitle }}
      </button>
    </template>
  </ts-modal>
</template>

<script lang="ts">
export interface ConfirmationConfiguration {
  title: string;
  message: string;
  type: 'danger' | 'primary' | 'info';
  cancelActionTitle: string;
  confirmActionTitle: string;
  confirmationCode: string | null;
  context?: unknown;
  modalDialogClass?: string | null;
}

export type ConfirmationFn = () => void;
</script>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import Rails from '@rails/ujs'
import TsModal from './ts-modal.vue'
import Alert from './alert.vue'
import { isNonEmptyString } from '../js-utils'

const emit = defineEmits<{
  'confirmed': [context: unknown]
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>('modal')

let triggerEl: HTMLElement | null = null
const confirmationCode = ref('')
const confirmationCodeInvalid = ref(false)
const confirmation = ref<ConfirmationConfiguration>({
  title: 'Action confirmation',
  message: 'Are you sure?',
  type: 'danger',
  cancelActionTitle: 'Cancel',
  confirmActionTitle: 'Confirm',
  confirmationCode: null,
  context: null,
})
let confirmationFn: ConfirmationFn | null = null

const requireConfirmationCode = computed((): boolean => {
  return isNonEmptyString(confirmation.value.confirmationCode)
})

const show = (triggerElement: HTMLElement | null, confirmationCfg: ConfirmationConfiguration, fn?: ConfirmationFn): void => {
  if (!modal.value?.isOpen) {
    triggerEl = triggerElement
    confirmation.value = confirmationCfg
    confirmationCode.value = ''
    confirmationCodeInvalid.value = false
    confirmationFn = fn ? fn : null
    modal.value?.show()
  }
}

const hide = (): void => {
  modal.value?.hide()
}

const onConfirmAction = (): void => {
  confirmationCodeInvalid.value = false
  if (isNonEmptyString(confirmation.value.confirmationCode)) {
    if (confirmation.value.confirmationCode !== confirmationCode.value) {
      confirmationCodeInvalid.value = true
      return
    }
  }
  emit('confirmed', confirmation.value.context)
  if (confirmationFn) {
    confirmationFn()
  }

  if (triggerEl) {
    const old = Rails.confirm
    Rails.confirm = function(): boolean { return true }
    triggerEl.click()
    Rails.confirm = old
  }
}

defineExpose({
  hide,
  show,
})
</script>
